import {
  Dialog as MuiDialog,
  DialogProps as MuiDialogProps,
  Stack,
  StackProps,
  Typography,
  TypographyProps,
} from '@mui/material';
import { forwardRef, ReactNode, useEffect, useImperativeHandle, useState } from 'react';

import { CloseButton } from '@/components/ds';

export interface DialogRef {
  open: () => void;
  close: () => void;
}

export interface DialogProps extends Omit<MuiDialogProps, 'open' | 'onClose'> {
  header?: ReactNode;
  title?: string;
  titleProps?: TypographyProps;
  subtitle?: string;
  subtitleProps?: TypographyProps;
  containerProps?: StackProps;
  actions?: ReactNode;
  actionsContainerProps?: StackProps;
  footer?: ReactNode;
  width?: number;
  minHeight?: number;
  disableClose?: boolean;
  disableBackdropClick?: boolean;
  error?: Error | null;
  open?: boolean;
  onClose?: () => void;
}

export const Dialog = forwardRef<DialogRef, DialogProps>(
  (
    {
      header,
      title,
      titleProps,
      subtitle,
      subtitleProps,
      containerProps,
      actions,
      actionsContainerProps,
      footer,
      width,
      minHeight,
      disableClose,
      disableBackdropClick,
      error,
      open: externalIsOpen,
      onClose,
      children,
      ...rest
    },
    ref,
  ) => {
    const isControlled = externalIsOpen !== undefined;
    const [isOpen, setIsOpen] = useState(externalIsOpen ?? false);
    const [shiftX, setShiftX] = useState(0);
    const rootSlot = rest.slotProps?.root as { sx?: object } | undefined;
    const paperSlot = rest.slotProps?.paper as { sx?: object } | undefined;

    useEffect(() => {
      const measure = () => {
        const main = document.getElementById('dashboard-main-content');
        if (main) {
          const r = main.getBoundingClientRect();
          const mainCenterX = r.left + r.width / 2;
          const viewportCenterX = window.innerWidth / 2;
          setShiftX(mainCenterX - viewportCenterX);
        }
      };
      measure();
      const sidebar = document.getElementById('dashboard-left-sidebar');
      const main = document.getElementById('dashboard-main-content');
      let ro: ResizeObserver | undefined;
      if (typeof ResizeObserver !== 'undefined') {
        ro = new ResizeObserver(measure);
        if (sidebar) ro.observe(sidebar);
        if (main) ro.observe(main);
      }
      window.addEventListener('resize', measure);
      const interval = window.setInterval(measure, 50);
      const timeout = window.setTimeout(() => window.clearInterval(interval), 400);
      return () => {
        ro?.disconnect();
        window.removeEventListener('resize', measure);
        window.clearInterval(interval);
        window.clearTimeout(timeout);
      };
    }, [isOpen]);

    const open = () => {
      if (!isControlled) {
        setIsOpen(true);
      }
    };

    const close = () => {
      if (disableClose) {
        return;
      }
      if (!isControlled) {
        setIsOpen(false);
      }
      onClose?.();
    };

    useImperativeHandle(ref, () => ({
      open,
      close,
    }));

    useEffect(() => {
      if (isControlled) {
        setIsOpen(externalIsOpen);
      }
    }, [externalIsOpen, isControlled]);

    return (
      <MuiDialog
        {...rest}
        open={isOpen}
        sx={{
          zIndex: (theme) => theme.zIndex.modal + 100,
          ...rest.sx,
        }}
        onClose={(_, reason) => {
          if (reason === 'backdropClick' && disableBackdropClick) return;
          close();
        }}
        disableEscapeKeyDown={disableClose}
        slotProps={{
          ...rest.slotProps,
          root: {
            ...rest.slotProps?.root,
            sx: {
              zIndex: (theme) => `${theme.zIndex.modal + 200} !important`,
              ...rootSlot?.sx,
            },
          },
          paper: {
            ...rest.slotProps?.paper,
            sx: {
              width,
              maxWidth: '100%',
              minHeight,
              // the app's dialog material (as the RFQ and records windows): solid surface, its sheen and drop, no stroke
              borderRadius: '24px',
              backgroundColor: 'var(--tx-dialog) !important',
              backgroundImage: 'var(--tx-surface-light) !important',
              boxShadow: '0 40px 120px -30px rgba(0,0,0,0.8)',
              border: 'none',
              animation: 'hxDialogIn 0.32s cubic-bezier(0.32, 0.72, 0, 1)',
              '@keyframes hxDialogIn': {
                from: { opacity: 0, transform: `translateX(${shiftX}px) translateY(8px) scale(0.97)` },
                to: { opacity: 1, transform: `translateX(${shiftX}px)` },
              },
              transform: `translateX(${shiftX}px)`,
              ...paperSlot?.sx,
            },
          },
        }}>
        {!disableClose && (
          <CloseButton onClose={close} label="close" sx={{ position: 'absolute', top: 18, right: 18, zIndex: 1 }} />
        )}

        <Stack gap="24px" p="28px" flexGrow={1} justifyContent="space-between" {...containerProps}>
          {header}

          {(title || subtitle) && (
            <Stack gap="6px">
              {title && (
                <Typography
                  component="p"
                  {...titleProps}
                  sx={{
                    pr: 6,
                    fontSize: 22,
                    fontWeight: 300,
                    letterSpacing: '-0.025em',
                    lineHeight: 1.2,
                    color: 'var(--tx-text)',
                    ...titleProps?.sx,
                  }}>
                  {title}
                </Typography>
              )}
              {subtitle && (
                <Typography
                  component="p"
                  {...subtitleProps}
                  sx={{ fontSize: 13, fontWeight: 500, color: 'var(--tx-muted)', lineHeight: 1.5, ...subtitleProps?.sx }}>
                  {subtitle}
                </Typography>
              )}
            </Stack>
          )}

          {children}

          {actions && (
            <Stack direction="row" gap="10px" justifyContent="flex-end" {...actionsContainerProps}>
              {actions}
            </Stack>
          )}

          {footer}

          {error && (
            <Typography variant="body2" color="error" textAlign="center">
              {error.message}
            </Typography>
          )}
        </Stack>
      </MuiDialog>
    );
  },
);
